// AI model catalog (Phase 5c). Insert-only — no in-place edits to historical
// rows. The grid shows the full history; "New rate row" + "Deactivate" +
// "Sync all" are the three mutating operations.

import { useEffect, useState } from 'react';
import { Grid, GridColumn } from '@progress/kendo-react-grid';
import type { GridCustomCellProps } from '@progress/kendo-react-grid';
import { Button } from '@progress/kendo-react-buttons';
import { Notification, NotificationGroup } from '@progress/kendo-react-notification';
import type { AiModelCreateRequest, AiModelDto, RegionSyncResult } from '@shared/api-types';
import { adminApiFetch } from '../../lib/adminApiClient';
import { useElementWidth, toResponsiveWidth } from '@shared/useElementWidth';

export function ModelCatalog() {
  const [rows, setRows] = useState<AiModelDto[] | null>(null);
  // Container-measured column shedding, matching the customer app's grids: the
  // grid's own width, not the viewport's, because the drawer rail changes what
  // is available by ~200px. See DESIGN.md ("Responsive").
  const [gridRef, gridWidth] = useElementWidth();
  const w = toResponsiveWidth(gridWidth);
  const [toast, setToast] = useState<string | null>(null);

  const reload = async () => {
    const data = await adminApiFetch<AiModelDto[]>('/api/admin/ai-models');
    setRows(data);
  };

  useEffect(() => {
    let active = true;
    void (async () => {
      const data = await adminApiFetch<AiModelDto[]>('/api/admin/ai-models');
      if (active) setRows(data);
    })();
    return () => {
      active = false;
    };
  }, []);

  const addRow = async () => {
    const deployment = window.prompt('Foundry deployment name (e.g. gpt-5.4)');
    if (!deployment) return;
    const display = window.prompt('Display name', deployment) ?? deployment;
    const inputPerCredit = Number(window.prompt('Input tokens per credit', '1000') ?? '1000');
    const outputPerCredit = Number(window.prompt('Output tokens per credit', '250') ?? '250');
    const price = Number(window.prompt('Credit price USD', '0.001') ?? '0.001');
    const maxOutRaw = window.prompt('Max output tokens (blank = model default)', '');
    const maxOut = maxOutRaw && maxOutRaw.trim() !== '' ? Number(maxOutRaw) : null;
    try {
      const body: AiModelCreateRequest = {
        foundryDeploymentName: deployment,
        displayName: display,
        inputTokensPerCredit: inputPerCredit,
        outputTokensPerCredit: outputPerCredit,
        creditPriceUsd: price,
        effectiveFromUtc: new Date().toISOString(),
        maxOutputTokens: maxOut,
      };
      await adminApiFetch<AiModelDto>('/api/admin/ai-models', { method: 'POST', body });
      await reload();
      setToast('New rate row added.');
    } catch (err) {
      setToast(err instanceof Error ? err.message : 'Add failed.');
    }
  };

  const deactivate = async (id: string) => {
    if (!window.confirm('Deactivate this model row?')) return;
    try {
      await adminApiFetch<AiModelDto>(`/api/admin/ai-models/${id}/deactivate`, { method: 'PUT' });
      await reload();
      setToast('Deactivated.');
    } catch (err) {
      setToast(err instanceof Error ? err.message : 'Deactivate failed.');
    }
  };

  const syncAll = async () => {
    try {
      const result = await adminApiFetch<RegionSyncResult>('/api/admin/ai-models/sync-all', { method: 'POST' });
      const summary = result.regions.map((r) => `${r.region.toUpperCase()}: ${r.status}`).join(' · ');
      setToast(`Sync: ${summary}`);
    } catch (err) {
      setToast(err instanceof Error ? err.message : 'Sync failed.');
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h2 style={{ margin: 0 }}>Model catalog</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button onClick={() => void syncAll()}>Sync to all regions</Button>
          <Button themeColor="primary" onClick={() => void addRow()}>New rate row</Button>
        </div>
      </div>
      <div ref={gridRef}>
      <Grid data={rows ?? []} style={{ height: 'calc(100dvh - 200px)' }}>
        {/* The widest grid in the admin app. Shed the effective-date window
            first (it is only consulted when auditing a price change), then the
            token-ratio detail, keeping the deployment name (identity), Active
            (state) and the price. */}
        <GridColumn field="foundryDeploymentName" title="Foundry" />
        {w >= 1000 && <GridColumn field="displayName" title="Display" />}
        <GridColumn field="isActive" title="Active" width="80px" />
        {w >= 900 && <GridColumn field="inputTokensPerCredit" title="In/credit" width="100px" />}
        {w >= 900 && <GridColumn field="outputTokensPerCredit" title="Out/credit" width="100px" />}
        <GridColumn field="creditPriceUsd" title="USD/credit" width="110px" />
        {w >= 780 && <GridColumn field="maxOutputTokens" title="Max out tok" width="110px" />}
        {w >= 1180 && <GridColumn field="effectiveFromUtc" title="From" width="160px" />}
        {w >= 1180 && <GridColumn field="effectiveToUtc" title="To" width="160px" />}
        <GridColumn
          title="Actions"
          width="120px"
          cells={{ data: (props: GridCustomCellProps) => {
            const row = props.dataItem as AiModelDto;
            return (
              <td>
                {row.isActive && row.effectiveToUtc == null && (
                  <Button size="small" onClick={() => void deactivate(row.id)}>Deactivate</Button>
                )}
              </td>
            );
          } }}
        />
      </Grid>
      </div>

      <NotificationGroup style={{ position: 'fixed', right: 16, bottom: 16 }}>
        {toast && (
          <Notification type={{ style: 'info', icon: true }} closable onClose={() => setToast(null)}>
            <span>{toast}</span>
          </Notification>
        )}
      </NotificationGroup>
    </div>
  );
}
